import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { api } from '@/api/sensors';
import { SensorDataResponse } from '@/types/api';
import { checkDataPointLimit } from '@/utils/performance';
import { useTank } from '@/contexts/TankContext';
import { CACHE_TIMES } from '@/config/cacheTimes';

interface UseSensorDataParams {
  startDate: Date;
  endDate: Date;
  parameters?: string[];
  excludeOutliers?: boolean;
  /** Set to false to disable the query (e.g., when data is provided from parent) */
  enabled?: boolean;
}

export function useSensorData({
  startDate,
  endDate,
  parameters = ['temp', 'pH', 'salinity'],
  excludeOutliers = true,
  enabled = true,
}: UseSensorDataParams): UseQueryResult<SensorDataResponse> {
  const { selectedTank } = useTank();
  const tankId = selectedTank?.id;

  return useQuery({
    queryKey: ['sensors', tankId, startDate, endDate, parameters, excludeOutliers],
    queryFn: async () => {
      // Normalize to full days: start at 00:00:00, end at next day 00:00:00
      const formatStartOfDay = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}T00:00:00Z`;
      };

      const formatEndOfDay = (date: Date) => {
        // Add 1 day to include full last day (query uses < end_date)
        const nextDay = new Date(date);
        nextDay.setDate(nextDay.getDate() + 1);
        const year = nextDay.getFullYear();
        const month = String(nextDay.getMonth() + 1).padStart(2, '0');
        const day = String(nextDay.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}T00:00:00Z`;
      };

      const response = await api.getSensorData({
        tank_id: tankId!,
        start_date: formatStartOfDay(startDate),
        end_date: formatEndOfDay(endDate),
        parameters,
        exclude_outliers: excludeOutliers,
      });

      // Verify data points are within limits
      if (response.data && !response.downsampled) {
        checkDataPointLimit(response.data);
      }

      return response;
    },
    enabled: enabled && !!tankId,
    staleTime: CACHE_TIMES.DATA,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}
